/*
  assets/gtender-workspace-pipeline.js — GTENDER-WORKSPACE-FE-WS04
  Owner: GTender Pro Pipeline workspace panel only.
  Identity / plan authority remains LittleGWebSession -> Core -> gtender_account.
  Pipeline persistence remains native-subject scoped behind /v1/tender/pipeline.
  Cross-capability tender selection is consumed from workspace intent; customers never type engineering tender IDs.
*/
(function () {
  'use strict';

  var VERSION = 'gtender-workspace-fe-ws04-02';
  var GLOBAL_KEY = '__GTENDER_WORKSPACE_PIPELINE_RUNTIME__';
  var ROOT_SELECTOR = '[data-gtender-dashboard-root][data-native-session-required]';
  var API_ORIGIN = 'https://api.glhksolution.com';
  var PIPELINE_URL = API_ORIGIN + '/v1/tender/pipeline';
  var REQUEST_TIMEOUT_MS = 10000;
  var STAGES = Object.freeze(['watching', 'review', 'bid_no_bid', 'prepare', 'closed']);
  var stateByRoot = new WeakMap();

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].boot === 'function') {
    window[GLOBAL_KEY].boot(document);
    return;
  }

  function list(scope, selector) {
    var target = scope || document;
    var output = [];
    if (target.matches && target.matches(selector)) output.push(target);
    if (target.querySelectorAll) Array.prototype.push.apply(output, target.querySelectorAll(selector));
    return output;
  }

  function clean(value) { return String(value == null ? '' : value).trim(); }
  function validId(value) {
    var id = clean(value);
    return id && id.length <= 240 && !/[\u0000-\u001f\u007f]/.test(id) ? id : '';
  }
  function isZh(root) { return /^zh(?:-|$)/i.test(clean(root && root.getAttribute('data-locale')) || clean(document.documentElement.lang)); }
  function copy(root, zh, en) { return isZh(root) ? zh : en; }
  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }
  function workspace() {
    return window.GTenderWorkspace && typeof window.GTenderWorkspace.getPanelHost === 'function' ? window.GTenderWorkspace : null;
  }

  function requestJson(root, state, url, options) {
    var session = window.LittleGWebSession;
    if (!session || typeof session.authenticatedFetch !== 'function') {
      return Promise.resolve({ ok: false, status: 0, code: 'native_session_unavailable', data: {} });
    }
    if (state.requestController) state.requestController.abort();
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    state.requestController = controller;
    var requestOptions = Object.assign({}, options || {});
    requestOptions.headers = Object.assign({ Accept: 'application/json' }, requestOptions.headers || {});
    if (controller) requestOptions.signal = controller.signal;
    var timeout = null;
    var deadline = new Promise(function (_, reject) {
      timeout = window.setTimeout(function () {
        if (controller) controller.abort();
        var error = new Error('GTender Pipeline request timed out.');
        error.code = 'gtender_pipeline_timeout';
        reject(error);
      }, REQUEST_TIMEOUT_MS);
    });
    return Promise.race([session.authenticatedFetch(url, requestOptions), deadline]).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (payload) {
        return {
          ok: response.ok && payload && payload.success === true,
          status: response.status,
          code: clean(payload && payload.error && payload.error.code),
          message: clean(payload && payload.error && payload.error.message),
          data: payload && payload.data && typeof payload.data === 'object' ? payload.data : {}
        };
      });
    }).catch(function (error) {
      return { ok: false, status: 0, code: clean(error && error.code) || 'network_error', message: '', data: {} };
    }).finally(function () {
      if (timeout !== null) window.clearTimeout(timeout);
      if (state.requestController === controller) state.requestController = null;
    });
  }

  function setStatus(state, text, tone) {
    state.status.textContent = text || '';
    state.status.setAttribute('data-tone', tone || 'neutral');
  }

  function accessChanged(root, state) {
    var ws = workspace();
    if (ws) ws.disable(root, 'pipeline');
    state.loaded = false;
    state.items = [];
    setStatus(state, copy(root, '正在重新核驗 GTender Pro 權限…', 'Revalidating GTender Pro access…'), 'warning');
    root.dispatchEvent(new CustomEvent('gtender-dashboard:access-refresh-required', { bubbles: true }));
  }

  function stageLabel(root, stage) {
    var labels = {
      watching: [copy(root, '監察中', 'Watching')],
      review: [copy(root, '評估中', 'Review')],
      bid_no_bid: [copy(root, 'Bid / No-bid', 'Bid / No-bid')],
      prepare: [copy(root, '準備投標', 'Prepare Your Bid')],
      closed: [copy(root, '已關閉', 'Closed')]
    };
    return labels[stage] ? labels[stage][0] : stage;
  }

  function render(root, state) {
    state.list.replaceChildren();
    state.count.textContent = String(state.items.length);
    if (!state.items.length) {
      var empty = element('div', 'gtender-workspace-pipeline__empty');
      empty.appendChild(element('h3', '', copy(root, 'Pipeline 暫無項目', 'No pipeline opportunities yet')));
      empty.appendChild(element('p', '', copy(root, '從 Saved 或招標詳情選擇招標，再加入 Pipeline；後端仍會核對 canonical tender。', 'Choose a tender from Saved or tender details, then add it to Pipeline; the backend still verifies the canonical tender.')));
      state.list.appendChild(empty);
      return;
    }

    state.items.forEach(function (item) {
      var tenderId = validId(item && item.tender_id);
      if (!tenderId) return;
      var card = element('article', 'gtender-workspace-pipeline__item');
      card.setAttribute('data-gtender-pipeline-item', tenderId);
      var head = element('div', 'gtender-workspace-pipeline__head');
      var title = element('div', '');
      title.appendChild(element('strong', '', tenderId));
      title.appendChild(element('span', 'gtender-workspace-pipeline__stage', stageLabel(root, clean(item.stage) || 'watching')));
      head.appendChild(title);
      var remove = element('button', 'gtender-workspace-pipeline__button gtender-workspace-pipeline__button--danger', copy(root, '移出 Pipeline', 'Remove'));
      remove.type = 'button';
      remove.setAttribute('data-gtender-pipeline-delete', tenderId);
      head.appendChild(remove);
      card.appendChild(head);

      if (item.decision === 'bid' || item.decision === 'no_bid') {
        card.appendChild(element('p', 'gtender-workspace-pipeline__decision', (item.decision === 'bid' ? 'BID' : 'NO-BID') + (clean(item.decision_reason) ? ' · ' + clean(item.decision_reason) : '')));
      }

      var form = element('form', 'gtender-workspace-pipeline__decision-form');
      form.setAttribute('data-gtender-pipeline-decision-form', tenderId);
      var select = document.createElement('select');
      select.name = 'decision';
      select.required = true;
      [['', copy(root, '選擇 Bid / No-bid', 'Choose Bid / No-bid')], ['bid', 'Bid'], ['no_bid', 'No-bid']].forEach(function (pair) {
        var option = document.createElement('option');
        option.value = pair[0];
        option.textContent = pair[1];
        select.appendChild(option);
      });
      var reason = document.createElement('input');
      reason.type = 'text';
      reason.name = 'reason';
      reason.maxLength = 1200;
      reason.required = true;
      reason.placeholder = copy(root, '決策原因（必填）', 'Decision reason (required)');
      var submit = element('button', 'gtender-workspace-pipeline__button', copy(root, '記錄決策', 'Record decision'));
      submit.type = 'submit';
      form.appendChild(select);
      form.appendChild(reason);
      form.appendChild(submit);
      card.appendChild(form);
      var error = element('p', 'gtender-workspace-pipeline__row-error', '');
      error.setAttribute('data-gtender-pipeline-row-error', tenderId);
      error.setAttribute('aria-live', 'polite');
      card.appendChild(error);
      state.list.appendChild(card);
    });
  }

  function renderBlocked(root, state, migration) {
    state.list.replaceChildren();
    var box = element('div', 'gtender-workspace-pipeline__empty');
    box.appendChild(element('h3', '', migration
      ? copy(root, '工作區 ownership 尚未完成', 'Workspace ownership migration is pending')
      : copy(root, 'Pipeline 暫時無法載入', 'Pipeline is temporarily unavailable')));
    box.appendChild(element('p', '', migration
      ? copy(root, '不會把舊 customer-owned Pipeline 推定為 native subject 資料。', 'Legacy customer-owned pipeline records are not assumed to belong to the native subject.')
      : copy(root, '現有資料保持不變。', 'Existing data remains unchanged.')));
    state.list.appendChild(box);
    state.count.textContent = '—';
  }

  function load(root, state, force) {
    if (!state.active || root.getAttribute('data-access-state') !== 'pro') return Promise.resolve(false);
    if (state.loading) return Promise.resolve(false);
    if (state.loaded && !force) return Promise.resolve(true);
    state.loading = true;
    root.setAttribute('data-pipeline-workspace-state', 'loading');
    setStatus(state, copy(root, '正在載入 Pipeline…', 'Loading pipeline…'), 'neutral');
    return requestJson(root, state, PIPELINE_URL + '?limit=200', { method: 'GET' }).then(function (result) {
      if (!state.active || root.getAttribute('data-access-state') !== 'pro') return false;
      if (result.status === 401 || result.status === 403) {
        accessChanged(root, state);
        root.setAttribute('data-pipeline-workspace-state', 'access-changed');
        return false;
      }
      if (result.status === 409 && result.code === 'workspace_ownership_migration_required') {
        state.loaded = false;
        renderBlocked(root, state, true);
        setStatus(state, copy(root, '未進行 ownership 推定。', 'No ownership assumption was made.'), 'warning');
        root.setAttribute('data-pipeline-workspace-state', 'ownership-blocked');
        return false;
      }
      if (!result.ok || !Array.isArray(result.data.items)) {
        state.loaded = false;
        renderBlocked(root, state, false);
        setStatus(state, copy(root, '沒有修改項目進度資料', 'No pipeline data was changed'), 'warning');
        root.setAttribute('data-pipeline-workspace-state', 'unavailable');
        return false;
      }
      state.items = result.data.items.filter(function (item) { return validId(item && item.tender_id); }).slice(0, 200);
      state.loaded = true;
      render(root, state);
      setStatus(state, copy(root, '已載入 native Pro Pipeline。', 'Native Pro pipeline loaded.'), 'success');
      root.setAttribute('data-pipeline-workspace-state', 'ready');
      return true;
    }).finally(function () { state.loading = false; });
  }

  function persistStage(root, state, tenderId, stage, source) {
    if (state.saving) return Promise.resolve(false);
    if (!tenderId || STAGES.indexOf(stage) < 0) {
      setStatus(state, copy(root, '請先從 Saved 或招標詳情選擇招標。', 'Choose a tender from Saved or tender details first.'), 'warning');
      return Promise.resolve(false);
    }
    state.saving = true;
    state.submit.disabled = true;
    setStatus(state, copy(root, '正在更新 Pipeline…', 'Updating pipeline…'), 'neutral');
    return requestJson(root, state, PIPELINE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tender_id: tenderId, stage: stage })
    }).then(function (result) {
      if (result.status === 401 || result.status === 403) {
        accessChanged(root, state);
        return false;
      }
      if (!result.ok || !result.data.item) {
        setStatus(state, result.status === 404
          ? copy(root, '找不到 canonical tender；未建立 Pipeline 記錄。', 'Canonical tender not found; no pipeline record was created.')
          : copy(root, '未能更新 Pipeline；現有資料保持不變。', 'Could not update the pipeline; existing data is unchanged.'), 'warning');
        return false;
      }
      state.selectedTenderId = tenderId;
      state.form.elements.tender_id.value = tenderId;
      state.form.elements.stage.value = stage;
      state.loaded = false;
      root.dispatchEvent(new CustomEvent('gtender-workspace:pipeline-updated', {
        bubbles: true,
        detail: { tender_id: tenderId, stage: stage, source: clean(source) || 'pipeline' }
      }));
      return load(root, state, true).then(function () {
        setStatus(state, copy(root, '已加入 Pipeline。', 'Added to Pipeline.'), 'success');
        return true;
      });
    }).finally(function () {
      state.saving = false;
      state.submit.disabled = !state.selectedTenderId;
    });
  }

  function setStage(root, state) {
    var tenderId = validId(state.form.elements.tender_id.value);
    var stage = clean(state.form.elements.stage.value);
    return persistStage(root, state, tenderId, stage, 'pipeline');
  }

  function consumePipelineIntent(root, state, detail) {
    var input = detail && typeof detail === 'object' ? detail : {};
    var tenderId = validId(input.tender_id);
    var stage = clean(input.stage) || 'watching';
    if (!tenderId || STAGES.indexOf(stage) < 0) {
      setStatus(state, copy(root, '未能識別所選招標；Pipeline 未變更。', 'The selected tender could not be identified; Pipeline is unchanged.'), 'warning');
      return;
    }
    if (root.getAttribute('data-access-state') !== 'pro') {
      accessChanged(root, state);
      return;
    }
    state.selectedTenderId = tenderId;
    state.form.elements.tender_id.value = tenderId;
    state.form.elements.stage.value = stage;
    state.submit.disabled = false;
    var ws = workspace();
    if (ws) {
      ws.enable(root, 'pipeline');
      ws.select(root, 'pipeline');
    }
    void persistStage(root, state, tenderId, stage, clean(input.source) || 'workspace-intent');
  }

  function rowError(state, id, text) {
    var node = state.host.querySelector('[data-gtender-pipeline-row-error="' + CSS.escape(id) + '"]');
    if (node) node.textContent = text || '';
  }

  function recordDecision(root, state, form) {
    var tenderId = validId(form.getAttribute('data-gtender-pipeline-decision-form'));
    var decision = clean(form.elements.decision.value);
    var reason = clean(form.elements.reason.value).slice(0, 1200);
    if (!tenderId || (decision !== 'bid' && decision !== 'no_bid') || !reason) {
      rowError(state, tenderId, copy(root, '請選擇 Bid / No-bid 並填寫原因。', 'Choose Bid / No-bid and enter a reason.'));
      return;
    }
    var button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    rowError(state, tenderId, copy(root, '正在記錄決策…', 'Recording decision…'));
    requestJson(root, state, PIPELINE_URL + '/' + encodeURIComponent(tenderId) + '/decision', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision: decision, reason: reason, inputs: {} })
    }).then(function (result) {
      if (result.status === 401 || result.status === 403) return accessChanged(root, state);
      if (!result.ok || !result.data.item) {
        rowError(state, tenderId, result.status === 404
          ? copy(root, 'Pipeline 項目不存在；未記錄決策。', 'Pipeline opportunity not found; decision was not recorded.')
          : copy(root, '未能記錄決策；現有資料保持不變。', 'Could not record the decision; existing data is unchanged.'));
        return;
      }
      state.loaded = false;
      return load(root, state, true);
    }).finally(function () { if (button && button.isConnected) button.disabled = false; });
  }

  function deleteItem(root, state, tenderId, button) {
    if (!tenderId || state.deleting.has(tenderId)) return;
    state.deleting.add(tenderId);
    button.disabled = true;
    rowError(state, tenderId, copy(root, '正在移除…', 'Removing…'));
    requestJson(root, state, PIPELINE_URL + '/' + encodeURIComponent(tenderId), { method: 'DELETE' }).then(function (result) {
      if (result.status === 401 || result.status === 403) return accessChanged(root, state);
      if (!result.ok || result.data.deleted !== true) {
        rowError(state, tenderId, copy(root, '未能移除；Pipeline 保持不變。', 'Could not remove it; the pipeline is unchanged.'));
        return;
      }
      state.items = state.items.filter(function (item) { return validId(item && item.tender_id) !== tenderId; });
      render(root, state);
      setStatus(state, copy(root, '已移出 Pipeline。', 'Removed from pipeline.'), 'success');
    }).finally(function () {
      state.deleting.delete(tenderId);
      if (button.isConnected) button.disabled = false;
    });
  }

  function makeShell(root, host) {
    host.replaceChildren();
    var shell = element('div', 'gtender-workspace-pipeline');
    shell.setAttribute('data-gtender-workspace-pipeline', '');
    var header = element('div', 'gtender-workspace-pipeline__header');
    var copyNode = element('div', '');
    copyNode.appendChild(element('p', 'gtender-workspace-pipeline__eyebrow', 'GTender Pro'));
    copyNode.appendChild(element('h2', '', 'Pipeline'));
    copyNode.appendChild(element('p', '', copy(root, '從 Saved 或招標詳情選擇招標，再管理 Bid / No-bid 工作流。', 'Choose a tender from Saved or tender details, then manage the Bid / No-bid workflow.')));
    header.appendChild(copyNode);
    var count = element('strong', 'gtender-workspace-pipeline__count', '—');
    header.appendChild(count);
    shell.appendChild(header);
    var status = element('p', 'gtender-workspace-pipeline__status', '');
    status.setAttribute('aria-live', 'polite');
    shell.appendChild(status);

    var form = element('form', 'gtender-workspace-pipeline__stage-form');
    var tenderLabel = element('label', '');
    tenderLabel.appendChild(element('span', '', copy(root, '已選招標', 'Selected tender')));
    var tenderInput = document.createElement('input');
    tenderInput.name = 'tender_id';
    tenderInput.type = 'text';
    tenderInput.maxLength = 240;
    tenderInput.readOnly = true;
    tenderInput.setAttribute('aria-readonly', 'true');
    tenderInput.placeholder = copy(root, '請從 Saved 或招標詳情選擇', 'Choose from Saved or tender details');
    tenderLabel.appendChild(tenderInput);
    var stageLabelNode = element('label', '');
    stageLabelNode.appendChild(element('span', '', copy(root, '階段', 'Stage')));
    var stage = document.createElement('select');
    stage.name = 'stage';
    STAGES.forEach(function (value) {
      var option = document.createElement('option');
      option.value = value;
      option.textContent = stageLabel(root, value);
      stage.appendChild(option);
    });
    stageLabelNode.appendChild(stage);
    var submit = element('button', 'gtender-workspace-pipeline__button gtender-workspace-pipeline__button--primary', copy(root, '更新所選招標', 'Update selected tender'));
    submit.type = 'submit';
    submit.disabled = true;
    form.appendChild(tenderLabel);
    form.appendChild(stageLabelNode);
    form.appendChild(submit);
    shell.appendChild(form);

    var toolbar = element('div', 'gtender-workspace-pipeline__toolbar');
    var refresh = element('button', 'gtender-workspace-pipeline__button', copy(root, '重新載入', 'Refresh'));
    refresh.type = 'button';
    refresh.setAttribute('data-gtender-pipeline-refresh', '');
    toolbar.appendChild(refresh);
    shell.appendChild(toolbar);
    var listNode = element('div', 'gtender-workspace-pipeline__list');
    shell.appendChild(listNode);
    host.appendChild(shell);
    return { shell: shell, count: count, status: status, form: form, submit: submit, list: listNode };
  }

  function activate(root, state) {
    var ws = workspace();
    if (!ws || root.getAttribute('data-access-state') !== 'pro') return;
    ws.enable(root, 'pipeline');
    if (root.getAttribute('data-workspace-tab') === 'pipeline') load(root, state, false);
  }

  function invalidate(root, state) {
    state.loaded = false;
    state.items = [];
    state.selectedTenderId = '';
    state.form.elements.tender_id.value = '';
    state.submit.disabled = true;
    if (state.requestController) state.requestController.abort();
    var ws = workspace();
    if (ws) ws.disable(root, 'pipeline');
    state.list.replaceChildren();
    state.count.textContent = '—';
    setStatus(state, '', 'neutral');
    root.removeAttribute('data-pipeline-workspace-state');
  }

  function mount(root) {
    if (!root || stateByRoot.has(root)) return;
    var ws = workspace();
    if (!ws) return;
    var host = ws.getPanelHost(root, 'pipeline');
    if (!host) return;
    var ui = makeShell(root, host);
    var state = {
      active: true,
      controller: new AbortController(),
      requestController: null,
      host: host,
      shell: ui.shell,
      count: ui.count,
      status: ui.status,
      form: ui.form,
      submit: ui.submit,
      list: ui.list,
      items: [],
      selectedTenderId: '',
      loaded: false,
      loading: false,
      saving: false,
      deleting: new Set()
    };
    stateByRoot.set(root, state);
    root.setAttribute('data-pipeline-workspace-owner', VERSION);

    state.form.addEventListener('submit', function (event) { event.preventDefault(); void setStage(root, state); }, { signal: state.controller.signal });
    state.shell.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.matches('[data-gtender-pipeline-decision-form]')) return;
      event.preventDefault();
      recordDecision(root, state, form);
    }, { signal: state.controller.signal });
    state.shell.addEventListener('click', function (event) {
      var target = event.target && event.target.closest ? event.target.closest('button') : null;
      if (!target || !state.host.contains(target)) return;
      if (target.matches('[data-gtender-pipeline-refresh]')) { event.preventDefault(); state.loaded = false; void load(root, state, true); return; }
      var id = validId(target.getAttribute('data-gtender-pipeline-delete'));
      if (id) { event.preventDefault(); deleteItem(root, state, id, target); }
    }, { signal: state.controller.signal });
    root.addEventListener('gtender-dashboard:pro-ready', function () { activate(root, state); }, { signal: state.controller.signal });
    root.addEventListener('gtender-dashboard:access-invalidated', function () { invalidate(root, state); }, { signal: state.controller.signal });
    root.addEventListener('gtender-workspace:tabchange', function (event) {
      if (event && event.detail && event.detail.tab === 'pipeline') void load(root, state, false);
    }, { signal: state.controller.signal });
    root.addEventListener('gtender-workspace:pipeline-add', function (event) {
      consumePipelineIntent(root, state, event && event.detail);
    }, { signal: state.controller.signal });
    activate(root, state);
  }

  function destroy(root) {
    var state = root ? stateByRoot.get(root) : null;
    if (!state) return;
    state.active = false;
    if (state.requestController) state.requestController.abort();
    state.controller.abort();
    var ws = workspace();
    if (ws) ws.disable(root, 'pipeline');
    state.host.replaceChildren();
    root.removeAttribute('data-pipeline-workspace-owner');
    root.removeAttribute('data-pipeline-workspace-state');
    stateByRoot.delete(root);
  }

  function boot(scope) { list(scope || document, ROOT_SELECTOR).forEach(mount); }
  function destroyAll(scope) { list(scope || document, ROOT_SELECTOR).forEach(destroy); }

  document.addEventListener('shopify:section:load', function () { window.setTimeout(function () { boot(document); }, 0); });
  document.addEventListener('shopify:section:unload', function (event) { destroyAll(event.target || document); });
  window.addEventListener('pageshow', function () { boot(document); });

  var api = Object.freeze({ version: VERSION, boot: boot, destroy: destroyAll, load: load });
  window[GLOBAL_KEY] = api;
  window.GTenderWorkspacePipeline = api;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  else boot(document);
})();
